import { useIsFetching, useIsMutating, useQueryClient } from '@zg-rentals/react-query5';
import { useHistory } from 'react-router-dom';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useEffect, useState } from 'react';

import { Box, Button, Divider, Heading, Modal } from '@zillow/constellation-10';

import { currentLeaseRenewalWizardScreenState, exitModalState } from '../../../atoms';
import type { WizardScreen } from '../../constants/stringConstants';
import { EXTERNAL_WIZARD_OPTIONS, WIZARD_SCREEN_TYPES } from '../../constants/stringConstants';
import { FullHeightLoadingMask } from '../../constants/styleConstants';
import { usePropertyInfoQuery } from '../../utils/queries';
import { Lease, Property } from '../../models';
import { useLeasingUrlParams } from '../../hooks/useLeasingUrlParams';
import AreYouPlanningToSell from '../../components/LeaseRenewal/Landlord/AreYouPlanningToSell';
import CongratulationsIsYourNewLeaseReady from '../../components/LeaseRenewal/Landlord/CongratulationsIsYourNewLeaseReady';
import ExitModal from '../../components/Widgets/ExitModal';
import HaveYouAlreadyFoundYourNewTenants from '../../components/LeaseRenewal/Landlord/HaveYouAlreadyFoundYourNewTenants';
import StepsToTakeToFindYourNextTenants from '../../components/LeaseRenewal/Landlord/StepsToTakeToFindYourNextTenants';
import UpdateYourCurrentTermsWithALeaseRenewalAgreement from '../../components/LeaseRenewal/Landlord/UpdateYourCurrentTermsWithALeaseRenewalAgreement';
import WhatAreYourPlansWithYourCurrentTenants from '../../components/LeaseRenewal/Landlord/WhatAreYourPlansWithYourCurrentTenants';
import WhatDoYouWantToDoWithYourLease from '../../components/LeaseRenewal/Landlord/WhatDoYouWantToDoWithYourLease';
import WhatDoYouWantToDoWithYourLeaseNoUpdate from '../../components/LeaseRenewal/Landlord/WhatDoYouWantToDoWithYourLeaseNoUpdate';
import WhatToDoWhenALeaseIsEnding from '../../components/LeaseRenewal/Landlord/WhatToDoWhenALeaseIsEnding';
import YoureAllSet from '../../components/LeaseRenewal/Landlord/YoureAllSet';
import YourUpcomingLeaseTermWillBeMonthToMonth from '../../components/LeaseRenewal/Landlord/YourUpcomingLeaseTermWillBeMonthToMonth';

export default function ManageYourLeaseModal() {
  const WIZARD_SCREENS: { [key in WIZARD_SCREEN_TYPES]: WizardScreen } = {
    [WIZARD_SCREEN_TYPES.WHAT_TO_DO_WHEN_A_LEASE_IS_ENDING]: {
      component: <WhatToDoWhenALeaseIsEnding />,
      primaryActionButtonText: 'Get started',
      showSecondaryActionButton: false,
      showPrimaryActionButton: true,
      needsSelection: false,
    },
    [WIZARD_SCREEN_TYPES.WHAT_ARE_THE_PLANS_WITH_YOUR_CURRENT_TENANTS]: {
      component: <WhatAreYourPlansWithYourCurrentTenants onRadioSelect={onRadioSelect} />,
      primaryActionButtonText: 'Continue',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: true,
    },
    [WIZARD_SCREEN_TYPES.WHAT_DO_YOU_WANT_TO_DO_WITH_YOUR_LEASE]: {
      component: <WhatDoYouWantToDoWithYourLease onRadioSelect={onRadioSelect} />,
      primaryActionButtonText: 'Continue',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: true,
    },
    [WIZARD_SCREEN_TYPES.WHAT_DO_YOU_WANT_TO_DO_WITH_YOUR_LEASE_NO_UPDATE]: {
      component: <WhatDoYouWantToDoWithYourLeaseNoUpdate onRadioSelect={onRadioSelect} />,
      primaryActionButtonText: 'Continue',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: true,
    },
    [WIZARD_SCREEN_TYPES.UPDATE_YOUR_CURRENT_TERMS_WITH_A_LEASE_RENEWAL_AGREEMENT]: {
      component: <UpdateYourCurrentTermsWithALeaseRenewalAgreement />,
      showSecondaryActionButton: true,
      showPrimaryActionButton: false,
      needsSelection: false,
    },
    [WIZARD_SCREEN_TYPES.YOUR_UPCOMING_LEASE_TERM_WILL_BE_MONTH_TO_MONTH]: {
      component: <YourUpcomingLeaseTermWillBeMonthToMonth />,
      primaryActionButtonText: 'Done',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: false,
    },
    [WIZARD_SCREEN_TYPES.HAVE_YOU_ALREADY_FOUND_YOUR_NEW_TENANTS]: {
      component: <HaveYouAlreadyFoundYourNewTenants onRadioSelect={onRadioSelect} />,
      primaryActionButtonText: 'Continue',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: true,
    },
    [WIZARD_SCREEN_TYPES.STEPS_TO_TAKE_TO_FIND_YOUR_NEXT_TENANTS]: {
      component: <StepsToTakeToFindYourNextTenants />,
      primaryActionButtonText: 'Go to your listing',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: false,
    },
    [WIZARD_SCREEN_TYPES.CONGRATULATIONS_IS_YOUR_NEW_LEASE_READY]: {
      component: <CongratulationsIsYourNewLeaseReady onRadioSelect={onRadioSelect} />,
      primaryActionButtonText: 'Create new lease',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: true,
    },
    [WIZARD_SCREEN_TYPES.ARE_YOU_PLANNING_TO_SELL]: {
      component: <AreYouPlanningToSell onRadioSelect={onRadioSelect} />,
      primaryActionButtonText: 'Continue',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: true,
    },
    [WIZARD_SCREEN_TYPES.YOURE_ALL_SET]: {
      component: <YoureAllSet />,
      primaryActionButtonText: 'Done',
      showSecondaryActionButton: true,
      showPrimaryActionButton: true,
      needsSelection: false,
    },
  };

  const { propertyId } = useLeasingUrlParams();
  const [, setIsExitModalOpen] = useRecoilState(exitModalState);
  const [currentLeaseRenewalWizardScreen, setCurrentLeaseRenewalWizardScreen] = useRecoilState(
    currentLeaseRenewalWizardScreenState,
  );
  const [isPrimaryButtonDisabled, setIsPrimaryButtonDisabled] = useState(false);
  const [radioButtonSelected, setRadioButtonSelected] = useState<WIZARD_SCREEN_TYPES | EXTERNAL_WIZARD_OPTIONS | null>(
    null,
  );
  const [wizardStepStack, setWizardStepStack] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const currentLeaseRenewalWizardScreenStateReset = useResetRecoilState(currentLeaseRenewalWizardScreenState);

  const propertyInfoQuery = usePropertyInfoQuery(propertyId);

  const history = useHistory();

  const isFetching = useIsFetching() > 0;
  const isMutating = useIsMutating() > 0;

  const queryClient = useQueryClient();

  const currentScreenObject = WIZARD_SCREENS[currentLeaseRenewalWizardScreen];

  useEffect(() => {
    if (currentScreenObject.needsSelection && radioButtonSelected === null) {
      setIsPrimaryButtonDisabled(true);
    } else {
      setIsPrimaryButtonDisabled(false);
    }
  }, [currentScreenObject]);

  useEffect(() => {
    currentLeaseRenewalWizardScreenStateReset();
    queryClient.invalidateQueries({
      predicate: ({ meta }) => meta?.tags?.includes('leasing:lease-status-change'),
    });
  }, []);

  function onRadioSelect(e: React.FormEvent<HTMLInputElement>) {
    const { value } = e.currentTarget;

    if (Object.values<string>(WIZARD_SCREEN_TYPES).includes(value)) {
      setRadioButtonSelected(value as WIZARD_SCREEN_TYPES);
    } else if (Object.values<string>(EXTERNAL_WIZARD_OPTIONS).includes(value)) {
      setRadioButtonSelected(value as EXTERNAL_WIZARD_OPTIONS);
    }
    setIsPrimaryButtonDisabled(false);
  }

  function onExitClick() {
    setIsExitModalOpen(true);
  }

  async function onPrimaryActionButtonClick() {
    setWizardStepStack((currentWizardStepStack) => [...currentWizardStepStack, currentLeaseRenewalWizardScreen]);
    if (currentLeaseRenewalWizardScreen === WIZARD_SCREEN_TYPES.WHAT_TO_DO_WHEN_A_LEASE_IS_ENDING) {
      setCurrentLeaseRenewalWizardScreen(WIZARD_SCREEN_TYPES.WHAT_ARE_THE_PLANS_WITH_YOUR_CURRENT_TENANTS);
    } else if (currentLeaseRenewalWizardScreen === WIZARD_SCREEN_TYPES.YOURE_ALL_SET) {
      history.push(Lease.getDashboardPath());
    } else if (
      currentLeaseRenewalWizardScreen === WIZARD_SCREEN_TYPES.YOUR_UPCOMING_LEASE_TERM_WILL_BE_MONTH_TO_MONTH
    ) {
      history.push(Lease.getDashboardPath());
    } else if (currentLeaseRenewalWizardScreen === WIZARD_SCREEN_TYPES.STEPS_TO_TAKE_TO_FIND_YOUR_NEXT_TENANTS) {
      setIsLoading(true);
      window.location.href = Property.getPropertyDashboardOverviewPath(propertyId);
    } else {
      if (Object.values<string>(WIZARD_SCREEN_TYPES).includes(radioButtonSelected)) {
        setCurrentLeaseRenewalWizardScreen(radioButtonSelected as WIZARD_SCREEN_TYPES);
      } else {
        if (radioButtonSelected === EXTERNAL_WIZARD_OPTIONS.CREATE_LEASE) {
          setIsLoading(true);
          history.push(Property.getCombinedOnboardingPath(propertyId));
        } else if (radioButtonSelected === EXTERNAL_WIZARD_OPTIONS.SEND_TO_PROPERTY_DASHBOARD) {
          setIsLoading(true);
          window.location.href = Property.getPropertyDashboardOverviewPath(propertyId);
        } else if (radioButtonSelected === EXTERNAL_WIZARD_OPTIONS.SEND_TO_SELLING) {
          setIsLoading(true);
          window.location.href = `/sell`;
        }
      }
    }
    setRadioButtonSelected(null);
  }

  function onSecondaryActionButtonClick() {
    setRadioButtonSelected(null);
    const previousStep = wizardStepStack[wizardStepStack.length - 1];
    setWizardStepStack((currentWizardStepStack) => currentWizardStepStack.slice(0, currentWizardStepStack.length - 1));
    setCurrentLeaseRenewalWizardScreen(previousStep);
  }

  function determinePrimaryButtonLabel() {
    if (radioButtonSelected === EXTERNAL_WIZARD_OPTIONS.SEND_TO_PROPERTY_DASHBOARD) {
      return 'Go to your listing';
    } else if (radioButtonSelected === EXTERNAL_WIZARD_OPTIONS.SEND_TO_SELLING) {
      return 'Sell your property on Zillow';
    } else if (radioButtonSelected === EXTERNAL_WIZARD_OPTIONS.CREATE_LEASE) {
      return 'Create new lease';
    } else return currentScreenObject.primaryActionButtonText;
  }

  return (
    <>
      <Modal.Root
        size="fullScreen"
        open
        onOpenChange={() => {}}
        useDismissProps={{ escapeKey: false, outsidePress: false }}
      >
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content>
              <Modal.Header
                css={{
                  display: 'flex',
                  paddingLeft: { base: 'layout.tight', lg: 'layout.loosest' },
                  paddingRight: { base: 'layout.tight', lg: 'layout.loosest' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Heading level={1} css={{ textStyle: 'responsive.heading-xs', marginRight: 'tight' }}>
                  {propertyInfoQuery?.data?.streetAddress}
                </Heading>
                <Button tone="neutral" emphasis="outlined" onClick={onExitClick}>
                  Exit
                </Button>
              </Modal.Header>
              <Divider />
              <Modal.Body>
                <FullHeightLoadingMask loading={isMutating || isFetching || isLoading}>
                  <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)' }}>
                    <Box css={{ gridColumnEnd: { lg: 'span 3' } }} />
                    <Box css={{ gridColumnEnd: { base: 'span 24', lg: 'span 18' } }}>
                      <Box css={{ marginTop: { base: 'none', lg: 'layout.loosest' } }} />
                      {WIZARD_SCREENS[currentLeaseRenewalWizardScreen].component}
                    </Box>
                    <Box css={{ gridColumnEnd: { lg: 'span 3' } }} />
                  </Box>
                </FullHeightLoadingMask>
              </Modal.Body>
              <Divider />
              <Modal.Footer
                css={{
                  display: 'flex',
                  paddingLeft: { base: 'layout.tight', lg: 'layout.loosest' },
                  paddingRight: { base: 'layout.tight', lg: 'layout.loosest' },
                  alignItems: 'center',
                  justifyContent: currentScreenObject.showSecondaryActionButton ? 'space-between' : 'flex-end',
                }}
              >
                {currentScreenObject.showSecondaryActionButton && (
                  <Button
                    tone="brand"
                    emphasis="outlined"
                    disabled={isMutating || isFetching || isLoading}
                    onClick={onSecondaryActionButtonClick}
                  >
                    Back
                  </Button>
                )}
                {currentScreenObject.showPrimaryActionButton && (
                  <Button
                    tone="brand"
                    emphasis="filled"
                    onClick={onPrimaryActionButtonClick}
                    disabled={isMutating || isFetching || isPrimaryButtonDisabled || isLoading}
                  >
                    {determinePrimaryButtonLabel()}
                  </Button>
                )}
              </Modal.Footer>
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
      <ExitModal />
    </>
  );
}
